import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../lib/i18n";
import { scrollToId } from "../lib/lenis";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-emerald-500/25 p-0.5 font-mono text-xs" data-testid="lang-toggle">
      {["el", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}-btn`} onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1.5 uppercase transition-colors duration-300 ${lang === l ? "bg-[#00E676] text-black" : "text-zinc-300 hover:text-white"}`}>
          {l === "el" ? "ΕΛ" : "EN"}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [["services", t.nav.services], ["work", t.nav.work], ["process", t.nav.process], ["contact", t.nav.contact]];
  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "bg-[#050706]/80 backdrop-blur-xl border-b border-emerald-500/15" : "border-b border-transparent"}`} data-testid="navbar">
      <div className="mx-auto flex h-[70px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Logo onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
        <nav className="hidden items-center gap-9 lg:flex">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-${id}-link`} className="nav-link font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white">
              {label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          <LangToggle />
          <button onClick={() => go("quote")} data-testid="nav-quote-btn" className="btn-primary">
            {t.nav.cta} <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="grid h-10 w-10 place-items-center rounded-full border border-emerald-500/30 text-white" aria-label="menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}
            className="border-t border-emerald-500/15 bg-[#050706]/95 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
            <div className="flex flex-col px-5 py-6">
              {links.map(([id, label], i) => (
                <motion.button key={id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
                  onClick={() => go(id)} data-testid={`mobile-nav-${id}-link`} className="border-b border-white/5 py-4 text-left font-display text-2xl font-bold uppercase text-white">
                  {label}
                </motion.button>
              ))}
              <button onClick={() => go("quote")} data-testid="mobile-quote-btn" className="btn-primary mt-6 justify-center">{t.nav.cta} <ArrowUpRight className="h-4 w-4" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
